import { useEffect, useState } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../components/Toast';
import { invalidateSupervisorQueries } from '../../lib/supervisorQueries';
import backend from '../../services/backend';
import { acceptedFinalizeMarker, FINALIZE_POLL_CAP_MS, finalizeLanded, finalizeRequestedKey, type FinalizeMarker } from './finalizePoll';
import { findingsKey, type SupervisorFindingsResponse } from './useSupervisorFindings';

const finalizeMutationKey = (projectId: string | undefined) => ['finalizeProject', projectId] as const;

/** Finalize Project, shared by the header's Actions menu and the Corrections tab's pending bar. */
export function useFinalizeProject(projectId: string | undefined) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: finalizeMutationKey(projectId),
    mutationFn: () => backend.finalizeProject(projectId!).then((res) => res.data),
    // The finalize boundary, taken before the POST: a fast handler can stamp `finalizedAt` before the
    // response reaches the browser. `before` is the stamp to wait past (server clock only).
    onMutate: (): FinalizeMarker => ({
      at: Date.now(),
      before: projectId ? queryClient.getQueryData<SupervisorFindingsResponse>(findingsKey(projectId))?.finalizedAt : undefined,
    }),
    onSuccess: (data, _vars, context) => {
      toast.success('Project finalized');
      if (!projectId) return;
      // POST /finalize only queues the handler: record the request boundary so the findings query
      // polls (finalizePollInterval) until the handler stamps `finalizedAt`, then refresh now.
      // Without an observer the entry's gc timer (default 5 min) starts at creation and `setQueryData`
      // on an existing entry does not restart it: a repeat finalize would lose its poll early. Recreate it.
      const key = finalizeRequestedKey(projectId);
      queryClient.removeQueries({ queryKey: key, exact: true });
      queryClient.setQueryData<FinalizeMarker>(key, acceptedFinalizeMarker(context ?? { at: Date.now(), before: undefined }, data.finalizedAt));
      void invalidateSupervisorQueries(queryClient, projectId);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

/**
 * True while a finalize is in flight: the request itself, then the queued handler until it stamps
 * `finalizedAt` (at most FINALIZE_POLL_CAP_MS). Both finalize entry points read it, so a
 * second click cannot rotate the finalization token and queue another finalize. The Header owns an
 * active findings observer (`useSupervisorFindings`), so the value it passes is live while any
 * project tab is open. `finalizedAt` is the findings response's, undefined until it loads.
 */
export function useFinalizeBusy(projectId: string | undefined, finalizedAt: string | null | undefined): boolean {
  const mutating = useIsMutating({ mutationKey: finalizeMutationKey(projectId) }) > 0;
  // Observes the marker without fetching it; subscribing also keeps the entry alive.
  const { data: marker } = useQuery<FinalizeMarker | null>({
    queryKey: finalizeRequestedKey(projectId ?? ''),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  const requestedAt = marker?.at ?? null;
  // The request whose poll cap has passed. A timer, not `Date.now()` in render: render must stay pure,
  // and a findings poll that returns unchanged data does not re-render, so busy would stick.
  const [capPassedFor, setCapPassedFor] = useState<number | null>(null);
  useEffect(() => {
    if (requestedAt == null) return;
    const timer = setTimeout(() => setCapPassedFor(requestedAt), requestedAt + FINALIZE_POLL_CAP_MS - Date.now());
    return () => clearTimeout(timer);
  }, [requestedAt]);
  // Clear the marker as soon as a fetch shows the finalize landed (`finalizedAt` changed since the
  // request). Not on a pending count of 0: with no correction waiting it is 0 before the handler runs.
  const landed = marker != null && finalizeLanded(finalizedAt, marker);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (projectId && landed) {
      // Two mounted callers (Header, Corrections tab) see the same render snapshot; the live read
      // lets only the first effect clear the marker and invalidate.
      if (queryClient.getQueryData(finalizeRequestedKey(projectId)) == null) return;
      queryClient.setQueryData(finalizeRequestedKey(projectId), null);
      // The finalize landed, so the entity projection (autofixes) and precision changed. The
      // onSuccess invalidation ran at enqueue time, before the handler. The null marker stops a repeat.
      void invalidateSupervisorQueries(queryClient, projectId);
    }
  }, [projectId, landed, queryClient]);
  const awaitingRun = requestedAt != null && capPassedFor !== requestedAt && !landed;
  return mutating || awaitingRun;
}
